import { Link } from "react-router-dom";
import {
  ArrowRight, Activity, Atom, Sprout, Flame, Sun, Sparkles,
  Database, Cpu, ChefHat, Hotel, Brain, Repeat,
} from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import Reveal from "@/components/Reveal";
import GoldCorners from "@/components/GoldCorners";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/s0rm07o0_IMG_2192.png";
const SECTION1_IMG =
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/c7itkshs_9D73C893-6699-46EE-A6BA-C73A4AAB96A3.png";
const STATS_IMG =
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/lc4fbwjf_e00e352c-2e3b-4273-b932-cbc6351e93a8.jpeg";

const PILLAR_ICONS = [Activity, Atom, Sprout, Flame, Sun, Sparkles];

// System steps (kept hardcoded for translation simplicity — most visible parts use t.method)
const SYSTEM_STEPS = [
  {
    icon: Database,
    n: "Step 1",
    t: "Bio-Intelligence Input",
    sub: "The Foundation — Comprehensive Data Collection",
    desc: "We gather comprehensive biological and lifestyle data to create a complete picture of each guest's unique health status and goals.",
    cols: [
      { h: "Biomarkers", items: ["Glucose and insulin sensitivity", "Lipid profile (cholesterol, triglycerides)", "Inflammatory markers (hs-CRP)", "Liver and kidney function", "Nutrient status and deficiencies", "Metabolic rate and body composition"] },
      { h: "Wearable & Behavioral Data", items: ["Heart rate variability (HRV) and sleep quality", "Activity levels and exercise patterns", "Recovery metrics and stress levels", "Digestion patterns and food tolerance", "Energy and mood fluctuations"] },
      { h: "Individual Goals", items: ["Longevity and disease prevention", "Weight management and body composition", "Performance and athletic optimisation", "Recovery and resilience", "Mental clarity and cognitive performance", "Sleep quality and energy"] },
    ],
    output: "Metabolic Identity Profile™",
  },
  { icon: Cpu, n: "Step 2", t: "Nutritional Intelligence Engine™", sub: "The Brain — Data-Driven Protocol Generation", desc: "Our proprietary system processes the Metabolic Identity Profile through six interconnected optimisation modules.", list: [["Metabolic Precision Engine", "Glucose response, insulin sensitivity, lipid metabolism → optimal macronutrient ratios and meal timing."], ["Cellular Optimization Engine", "Nutrients for mitochondrial support, ATP production and cellular repair, calibrated to activity, stress and recovery needs."], ["Microbiome Engineering Engine", "Optimal prebiotic fibres, polyphenols and fermented foods for current microbiome status and diversity goals."], ["Anti-Inflammatory Engine", "Specific ingredients and combinations that modulate inflammatory response while supporting hormonal balance."], ["Circadian Optimization Engine", "Meal timing, portion sizes and macronutrient distribution aligned with circadian rhythms and individual sleep patterns."], ["Personalization Engine", "Continuously adapts all parameters based on feedback loops, biomarker changes and guest preferences."]], output: "Dynamic Nutritional Protocol™" },
  { icon: ChefHat, n: "Step 3", t: "Culinary Translation Layer", sub: "The Art — Science Into Exceptional Dining", desc: "Expert chefs and nutritionists translate scientific protocols into exceptional dining experiences that are both nourishing and extraordinary.", list: [["Ingredient Selection Logic", "Sourced for bioavailability, anti-inflammatory potential, microbiome impact and synergistic effects."], ["Functional Recipe Development", "Every recipe engineered to deliver specific nutritional outcomes while maintaining exceptional taste, texture and presentation."], ["Menu Engineering", "Daily menus optimise nutrient timing, circadian alignment and cumulative health impact across all meals."], ["Sensory & Emotional Design", "Every dish delights while delivering measurable health benefits — nutrition is biochemistry and emotion."]], output: "Precision Longevity Menus™" },
  { icon: Hotel, n: "Step 4", t: "Hospitality Integration", sub: "The Experience — Seamless Implementation Across Touchpoints", desc: "The system integrates across every guest touchpoint, creating a cohesive longevity journey.", list: [["Restaurant Dining", "Each meal is a precision-engineered experience, optimised for protocol and circadian rhythm."], ["In-Room Dining", "Personalised meal delivery that maintains protocol adherence and convenience."], ["Spa & Recovery Programs", "Nutrition timing and composition optimised for recovery, stress reduction and cellular repair."], ["Retreat Protocols", "Multi-day or multi-week programs combining nutrition, movement, sleep optimisation and stress management."], ["Guest Education", "Ongoing communication about why each meal matters and how it contributes to health goals."]], output: "Longevity Guest Journey™" },
  { icon: Brain, n: "Step 5", t: "Behavioral Design", sub: "The Psychology — Sustainable Health Habit Formation", desc: "Lasting health transformation requires behavioural change. The system embeds proven strategies for habit formation and adherence.", list: [["Habit Formation Strategies", "Meal timing, presentation and communication designed to create positive associations and automatic behaviours."], ["Intelligent Nudging", "Subtle cues guide guests toward optimal choices without feeling restrictive."], ["Adherence Optimisation", "Adherence patterns monitored; protocols adjusted to maintain motivation and results."], ["Emotional Engagement", "Guests connect emotionally to their journey, understanding the 'why' behind each recommendation."]], output: "Sustained Behavioral Change™" },
  { icon: Repeat, n: "Step 6", t: "Continuous Optimization Loop™", sub: "The Evolution — Intelligent System Learning", desc: "The system doesn't remain static. It learns, evolves and improves over time.", list: [["Outcome Tracking", "Health improvements measured across biomarkers, wearable data and guest-reported metrics."], ["Data Refinement", "More data per guest sharpens our understanding of what works best for them."], ["Protocol Evolution", "Protocols continuously adjusted based on results, feedback and emerging research."], ["AI Learning", "Algorithms learn from aggregate data, identifying patterns and optimising recommendations."], ["Continuous Improvement", "Each iteration makes the system smarter, more effective, more personalised."]], output: "Measurable, Sustained Health Transformation" },
];

function StagedImage({ src, alt }) {
  return (
    <div className="relative">
      <div aria-hidden className="absolute -inset-6 sm:-inset-8 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(26, 80, 115, 0.35), rgba(15, 44, 64, 0.18) 45%, transparent 75%)", filter: "blur(24px)" }} />
      <div aria-hidden className="absolute -inset-4 pointer-events-none" style={{ background: "radial-gradient(ellipse at 70% 30%, rgba(199,162,108,0.10), transparent 55%)", filter: "blur(20px)" }} />
      <div className="relative aspect-[4/5] sm:aspect-[16/11] overflow-hidden" style={{ boxShadow: "0 30px 60px -20px rgba(18, 60, 90, 0.55), 0 14px 28px -10px rgba(7, 14, 19, 0.6)" }}>
        <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(0.78) contrast(1.08) saturate(1.05)" }} />
        <div className="absolute inset-0 mix-blend-overlay opacity-55" style={{ background: "linear-gradient(135deg, rgba(199,162,108,0.28) 0%, rgba(168,132,90,0.18) 45%, rgba(7,14,19,0.45) 100%)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070E13]/75 via-[#070E13]/15 to-transparent" />
        <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 120px 20px rgba(7,14,19,0.55)" }} />
        <GoldCorners size={14} inset={-2} />
      </div>
    </div>
  );
}

export default function Method() {
  const { t } = useLang();
  const m = t.method;
  return (
    <div data-testid="page-method" className="bg-[#070E13] text-[#F4EEE8]">
      <section className="relative pt-40 pb-24 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0 motif-grid opacity-25" />
        <div className="absolute inset-0 motif-hex opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#070E13]/40 to-[#070E13]" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-7">
            <p className="overline gold-text">{m.eyebrow}</p>
            <h1 className="h-editorial text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[1.04]">
              {m.title}<sup className="font-serif text-2xl gold-text align-super">™</sup>
            </h1>
            <p className="mt-7 text-xl sm:text-2xl text-[#E8DFC8]/95 italic font-serif leading-snug max-w-2xl">{m.tagline}</p>
            <p className="mt-8 max-w-2xl text-lg text-[#D6D1C9] leading-relaxed">{m.lead}</p>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-5">
            <StagedImage src={HERO_IMG} alt="Precision Longevity Architecture" />
          </Reveal>
        </div>
      </section>

      <section className="border-y border-[#C7A26C]/15 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
          <Reveal className="lg:col-span-6 lg:order-2">
            <StagedImage src={SECTION1_IMG} alt="Beyond traditional nutrition" />
          </Reveal>
          <Reveal delay={150} className="lg:col-span-6 lg:order-1 lg:pe-8">
            <p className="overline gold-text">{m.s1Eyebrow}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{m.s1Title}</h2>
            <p className="mt-6 text-[#D6D1C9] text-lg leading-relaxed">{m.s1P1}</p>
            <p className="mt-4 text-[#D6D1C9]/85 leading-relaxed">{m.s1P2}</p>
            <p className="mt-4 text-[#D6D1C9]/85 leading-relaxed">{m.s1P3}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28 border-b border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <div className="max-w-3xl">
            <Reveal>
              <p className="overline gold-text">{m.s2Eyebrow}</p>
              <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{m.s2Title}</h2>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {m.pillars.map((p, i) => {
              const Icon = PILLAR_ICONS[i];
              return (
                <Reveal key={i} delay={(i % 2) * 80} className="relative bg-[#0C1821] border border-[#C7A26C]/20 hover:border-[#C7A26C]/55 transition-colors duration-500 p-8 lg:p-10">
                  <GoldCorners size={12} inset={-1} />
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-full border border-[#C7A26C]/55 flex items-center justify-center shrink-0">
                      <Icon size={18} className="text-[#C7A26C]" strokeWidth={1.4} />
                    </div>
                    <div>
                      <p className="overline gold-text">{m.pillarLabel} {p.n}</p>
                      <h3 className="font-serif text-2xl mt-3 leading-tight">{p.t}</h3>
                      <p className="text-xs text-[#C7A26C]/80 mt-2 tracking-wide">{p.focus}</p>
                    </div>
                  </div>
                  <p className="mt-5 text-[#D6D1C9] leading-relaxed text-sm">{p.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {p.bullets.map((b, j) => (
                      <li key={j} className="flex gap-3 text-sm text-[#D6D1C9]/90">
                        <span className="text-[#C7A26C] mt-2 leading-none">·</span>
                        <span className="leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 border-b border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <div className="max-w-3xl">
            <Reveal>
              <p className="overline gold-text">{m.s3Eyebrow}</p>
              <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{m.s3Title}</h2>
            </Reveal>
          </div>
          <div className="mt-14 space-y-6">
            {SYSTEM_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={i} delay={i * 60} className="relative bg-[#0C1821] border border-[#C7A26C]/20 hover:border-[#C7A26C]/45 transition-colors duration-500 p-8 lg:p-10">
                  <GoldCorners size={12} inset={-1} />
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
                    <div className="lg:col-span-3 flex lg:flex-col items-start gap-4">
                      <div className="w-12 h-12 rounded-full border border-[#C7A26C]/55 flex items-center justify-center shrink-0">
                        <Icon size={18} className="text-[#C7A26C]" strokeWidth={1.4} />
                      </div>
                      <div>
                        <p className="overline gold-text">{s.n}</p>
                        <h3 className="font-serif text-2xl mt-2 leading-tight">{s.t}</h3>
                      </div>
                    </div>
                    <div className="lg:col-span-9">
                      <p className="text-xs text-[#C7A26C]/80 tracking-wider uppercase">{s.sub}</p>
                      <p className="mt-3 text-[#D6D1C9] leading-relaxed">{s.desc}</p>
                      {s.cols && (
                        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                          {s.cols.map((c, j) => (
                            <div key={j}>
                              <p className="overline text-[#F4EEE8]/90">{c.h}</p>
                              <ul className="mt-3 space-y-1.5">
                                {c.items.map((it, k) => (
                                  <li key={k} className="flex gap-2 text-xs text-[#D6D1C9]/85">
                                    <span className="text-[#C7A26C] leading-none mt-1.5">·</span>
                                    <span>{it}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                      {s.list && (
                        <ul className="mt-5 space-y-3">
                          {s.list.map(([h, d], j) => (
                            <li key={j} className="flex gap-3 text-sm">
                              <span className="text-[#C7A26C] mt-1.5 leading-none">·</span>
                              <span className="text-[#D6D1C9]/95">
                                <span className="text-[#F4EEE8] font-medium">{h}.</span>{" "}
                                <span className="text-[#D6D1C9]/85">{d}</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-6 pt-5 border-t border-[#C7A26C]/15 flex items-center gap-3">
                        <span className="overline text-[#C7A26C]/80">{m.outputLabel}</span>
                        <span className="font-serif text-lg gold-text">{s.output}</span>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 border-b border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="relative">
              <div aria-hidden className="absolute -inset-8 sm:-inset-10 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(26, 80, 115, 0.45), rgba(15, 44, 64, 0.22) 45%, transparent 78%)", filter: "blur(32px)" }} />
              <div aria-hidden className="absolute -inset-6 pointer-events-none" style={{ background: "radial-gradient(ellipse at 70% 30%, rgba(199,162,108,0.16), transparent 55%)", filter: "blur(28px)" }} />
              <div className="relative aspect-[4/5] sm:aspect-[16/11] lg:aspect-[3/4] overflow-hidden" style={{ boxShadow: "0 40px 80px -20px rgba(18, 60, 90, 0.7), 0 20px 40px -10px rgba(7, 14, 19, 0.75)" }}>
                <img src={STATS_IMG} alt="Measurable outcomes" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "brightness(0.82) contrast(1.1) saturate(1.08)" }} />
                {/* Cinematic warm wash */}
                <div className="absolute inset-0 mix-blend-overlay opacity-60" style={{ background: "linear-gradient(135deg, rgba(199,162,108,0.32) 0%, rgba(168,132,90,0.22) 45%, rgba(7,14,19,0.55) 100%)" }} />
                {/* Bottom-up dark fade */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#070E13]/85 via-[#070E13]/25 to-transparent" />
                {/* Top atmospheric darkening */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#070E13]/45 via-transparent to-transparent" />
                {/* Inner vignette + golden inner glow */}
                <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 180px 35px rgba(7,14,19,0.7), inset 0 0 80px 12px rgba(199,162,108,0.10)" }} />
                <GoldCorners size={16} inset={-2} />
              </div>
            </div>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-7 lg:ps-6">
            <p className="overline gold-text">{m.s4Eyebrow}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{m.s4Title}</h2>
            <p className="mt-6 text-[#D6D1C9] leading-relaxed max-w-2xl">{m.s4Lead}</p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#C7A26C]/15 border border-[#C7A26C]/15">
              {m.outcomes.map((o, i) => {
                const isLastOdd = i === m.outcomes.length - 1 && m.outcomes.length % 2 === 1;
                return (
                  <div
                    key={i}
                    className={`relative bg-[#0C1821] p-7 lg:p-8 ${isLastOdd ? "sm:col-span-2" : ""}`}
                  >
                    <span className="absolute top-0 left-0 w-10 h-px bg-[#C7A26C]/70" />
                    <p className="font-serif text-5xl gold-text leading-none">{o.pct}</p>
                    <p className="mt-3 font-serif text-lg text-[#F4EEE8]">{o.t}</p>
                    <p className="mt-2 text-xs text-[#D6D1C9]/85 leading-relaxed">{o.desc}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-8 text-xs text-[#D6D1C9]/65 italic max-w-2xl">{m.s4Footnote}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28 border-b border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <div className="max-w-3xl">
            <Reveal>
              <p className="overline gold-text">{m.s5Eyebrow}</p>
              <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{m.s5Title}</h2>
              <p className="mt-6 text-[#D6D1C9] leading-relaxed">{m.s5Lead}</p>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {m.principles.map((p, i) => (
              <Reveal key={i} delay={i * 80} className="relative bg-[#0C1821] border border-[#C7A26C]/20 hover:border-[#C7A26C]/45 transition-colors duration-500 p-8 lg:p-10">
                <GoldCorners size={12} inset={-1} />
                <p className="font-serif text-5xl gold-text">{p.n}</p>
                <h3 className="font-serif text-2xl mt-5">{p.t}</h3>
                <p className="mt-4 text-[#D6D1C9] leading-relaxed">{p.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 border-b border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <div className="max-w-3xl">
            <Reveal>
              <p className="overline gold-text">{m.s6Eyebrow}</p>
              <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{m.s6Title}</h2>
              <p className="mt-6 text-[#D6D1C9] leading-relaxed">{m.s6Lead}</p>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {m.phases.map(([n, ti, d], i) => (
              <Reveal key={i} delay={i * 70} className="relative bg-[#0C1821] border border-[#C7A26C]/20 hover:border-[#C7A26C]/45 transition-colors duration-500 p-7 min-h-[280px] flex flex-col">
                <GoldCorners size={10} inset={-1} />
                <p className="overline gold-text">{n}</p>
                <h3 className="font-serif text-xl mt-4 leading-tight">{ti}</h3>
                <p className="mt-4 text-sm text-[#D6D1C9]/85 leading-relaxed">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 border-b border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <div className="max-w-3xl">
            <Reveal>
              <p className="overline gold-text">{m.s7Eyebrow}</p>
              <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{m.s7Title}</h2>
              <p className="mt-6 text-[#D6D1C9] leading-relaxed">{m.s7Lead}</p>
            </Reveal>
          </div>
          <div className="mt-12 divide-y divide-[#C7A26C]/15 border-y border-[#C7A26C]/15">
            {m.applications.map(([ti, d], i) => (
              <Reveal key={i} delay={i * 60} className="grid grid-cols-1 lg:grid-cols-12 gap-4 py-7 lg:py-8 group hover:bg-[#0C1821]/50 transition-colors px-2">
                <div className="lg:col-span-1 flex items-start">
                  <span className="font-serif text-3xl gold-text leading-none">0{i + 1}</span>
                </div>
                <h3 className="lg:col-span-4 font-serif text-2xl">{ti}</h3>
                <p className="lg:col-span-7 text-[#D6D1C9]/90 leading-relaxed">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-20 sm:py-28 border-b border-[#C7A26C]/15 overflow-hidden">
        <div className="absolute inset-0 motif-grid opacity-25" />
        <div className="absolute inset-0 motif-hex opacity-20" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(199,162,108,0.06), transparent 60%)" }} />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-10 lg:px-12 text-center">
          <Reveal>
            <p className="overline gold-text">{m.s8Eyebrow}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl lg:text-6xl mt-5">{m.s8Title}</h2>
            <p className="mt-8 text-lg text-[#D6D1C9] leading-relaxed max-w-3xl mx-auto">{m.s8P1}</p>
            <p className="mt-5 text-[#D6D1C9]/85 leading-relaxed max-w-3xl mx-auto">{m.s8P2}</p>
            <p className="mt-8 font-serif italic text-2xl gold-text">{m.s8Quote}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Reveal>
            <p className="overline gold-text">{m.ctaEyebrow}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl mt-5">{m.ctaTitle}</h2>
            <p className="mt-6 max-w-xl mx-auto text-[#D6D1C9] leading-relaxed">{m.ctaLead}</p>
            <div className="mt-10 flex flex-wrap items-center gap-4 justify-center">
              <Link to="/contact" data-testid="method-primary-cta" className="cta-premium inline-flex items-center gap-2 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] px-9 py-4 text-xs uppercase tracking-[0.24em] font-medium">
                {m.ctaPrimary} <ArrowRight size={16} />
              </Link>
              <Link to="/contact" data-testid="method-secondary-cta" className="inline-flex items-center gap-2 border border-[#F4EEE8]/35 text-[#F4EEE8] px-9 py-4 text-xs uppercase tracking-[0.24em] hover:border-[#C7A26C] hover:text-[#C7A26C] transition-colors">
                {m.ctaSecondary}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
